import React, { useState, useEffect } from 'react';
import { X, Mail, Smartphone, ArrowRight, ArrowLeft, Lock, Eye, EyeOff, Loader2, CheckCircle } from 'lucide-react';
import { useLanguage } from '../i18nContext';
import { sendVerificationCode, sendSmsCode, resetPassword, resetPasswordByPhone } from '../services/auth';

interface ForgotPasswordModalProps {
  onClose: () => void;
  onBackToLogin: () => void;
  mode?: 'reset' | 'change';
}

type ResetMethod = 'email' | 'phone';
type Step = 'method' | 'verify' | 'newPassword' | 'success';

export default function ForgotPasswordModal({ onClose, onBackToLogin, mode = 'reset' }: ForgotPasswordModalProps) {
  const { language } = useLanguage();
  const [method, setMethod] = useState<ResetMethod>('email');
  const [step, setStep] = useState<Step>('method');

  // 表单状态
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // 加载状态
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // 错误信息
  const [error, setError] = useState('');

  // 倒计时效果
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 发送验证码
  const handleSendCode = async () => {
    if (method === 'email' && !email) {
      setError(language === 'zh' ? '请输入邮箱' : 'Please enter email');
      return;
    }
    if (method === 'phone' && !phone) {
      setError(language === 'zh' ? '请输入手机号' : 'Please enter phone number');
      return;
    }

    setSendingCode(true);
    setError('');
    try {
      if (method === 'email') {
        await sendVerificationCode(email);
      } else {
        await sendSmsCode(phone, 'reset_password');
      }
      setCountdown(60);
      setStep('verify');
    } catch (e: any) {
      setError(e.message || (language === 'zh' ? '发送失败' : 'Failed to send'));
    } finally {
      setSendingCode(false);
    }
  };

  // 验证验证码并进入设置新密码
  const handleVerifyCode = () => {
    if (!code) {
      setError(language === 'zh' ? '请输入验证码' : 'Please enter verification code');
      return;
    }
    if (code.length < 4) {
      setError(language === 'zh' ? '验证码格式不正确' : 'Invalid verification code');
      return;
    }
    setError('');
    setStep('newPassword');
  };

  // 重置密码
  const handleResetPassword = async () => {
    if (!newPassword) {
      setError(language === 'zh' ? '请输入新密码' : 'Please enter new password');
      return;
    }
    if (newPassword.length < 6) {
      setError(language === 'zh' ? '密码至少6位' : 'Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(language === 'zh' ? '两次密码不一致' : 'Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');
    try {
      if (method === 'email') {
        await resetPassword({ email, verificationCode: code, newPassword });
      } else {
        await resetPasswordByPhone({ phone, code, newPassword });
      }
      setStep('success');
    } catch (e: any) {
      setError(e.message || (language === 'zh' ? '重置失败' : 'Reset failed'));
    } finally {
      setLoading(false);
    }
  };

  // 返回上一步
  const handleBack = () => {
    setError('');
    if (step === 'verify') {
      setStep('method');
    } else if (step === 'newPassword') {
      setStep('verify');
    }
  };

  // 获取步骤标题
  const getStepTitle = () => {
    switch (step) {
      case 'method':
        return mode === 'change'
          ? (language === 'zh' ? '修改密码' : 'Change Password')
          : (language === 'zh' ? '找回密码' : 'Reset Password');
      case 'verify':
        return language === 'zh' ? '验证身份' : 'Verify Identity';
      case 'newPassword':
        return language === 'zh' ? '设置新密码' : 'Set New Password';
      case 'success':
        return mode === 'change'
          ? (language === 'zh' ? '修改成功' : 'Password Changed')
          : (language === 'zh' ? '重置成功' : 'Reset Successful');
    }
  };

  // 获取步骤描述
  const getStepDescription = () => {
    switch (step) {
      case 'method':
        return mode === 'change'
          ? (language === 'zh' ? '选择验证方式修改您的密码' : 'Choose a method to change your password')
          : (language === 'zh' ? '选择验证方式找回您的密码' : 'Choose a method to reset your password');
      case 'verify':
        return language === 'zh'
          ? `验证码已发送至 ${method === 'email' ? email : phone}`
          : `Code sent to ${method === 'email' ? email : phone}`;
      case 'newPassword':
        return language === 'zh' ? '请设置您的新密码' : 'Please set your new password';
      case 'success':
        return mode === 'change'
          ? (language === 'zh' ? '您的密码已修改成功' : 'Your password has been changed successfully')
          : (language === 'zh' ? '您的密码已重置成功' : 'Your password has been reset successfully');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 isolate">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-brand-900/10 backdrop-blur-md transform-gpu transition-opacity duration-300 pointer-events-auto"
        onClick={onClose}
      ></div>

      <div className="relative w-full max-w-md bg-white rounded-modal shadow-modal overflow-hidden animate-in fade-in zoom-in-95 duration-500 border border-white z-10">
        <div className="p-8 md:p-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            {step !== 'method' && step !== 'success' ? (
              <button
                onClick={handleBack}
                className="p-2 text-gray-400 hover:text-brand-900 rounded-full hover:bg-gray-50 transition-all"
              >
                <ArrowLeft size={20} />
              </button>
            ) : (
              <div className="w-9"></div>
            )}
            <div className="text-center flex-1">
              <h2 className="text-2xl font-serif font-bold text-brand-900">{getStepTitle()}</h2>
              <p className="text-gray-400 text-sm mt-1">{getStepDescription()}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-300 hover:text-brand-900 rounded-full hover:bg-gray-50 transition-all"
            >
              <X size={20} />
            </button>
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: 选择验证方式 */}
          {step === 'method' && (
            <div className="space-y-6">
              {/* 方式选择 */}
              <div className="flex p-1 bg-surface rounded-2xl border border-surface-border">
                <button
                  onClick={() => { setMethod('email'); setError(''); }}
                  className={`flex-1 py-3 text-sm font-bold rounded-xl transition-all flex items-center justify-center gap-2
                  ${method === 'email'
                    ? 'bg-white text-brand-900 shadow-sm ring-1 ring-surface-border'
                    : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <Mail size={16} /> {language === 'zh' ? '邮箱验证' : 'Email'}
                </button>
                <button
                  onClick={() => { setMethod('phone'); setError(''); }}
                  className={`flex-1 py-3 text-sm font-bold rounded-xl transition-all flex items-center justify-center gap-2
                  ${method === 'phone'
                    ? 'bg-white text-brand-900 shadow-sm ring-1 ring-surface-border'
                    : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <Smartphone size={16} /> {language === 'zh' ? '手机验证' : 'Phone'}
                </button>
              </div>

              {/* 输入框 */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                  {method === 'email'
                    ? (language === 'zh' ? '邮箱地址' : 'Email Address')
                    : (language === 'zh' ? '手机号' : 'Phone Number')}
                </label>
                <input
                  type={method === 'email' ? 'email' : 'tel'}
                  value={method === 'email' ? email : phone}
                  onChange={(e) => method === 'email' ? setEmail(e.target.value) : setPhone(e.target.value)}
                  placeholder={method === 'email' ? 'name@company.com' : (language === 'zh' ? '请输入手机号' : 'Enter phone number')}
                  className="w-full h-12 px-4 rounded-xl border border-surface-border bg-surface text-sm focus:bg-white focus:border-brand-900 focus:ring-4 focus:ring-brand-900/5 transition-all outline-none"
                />
              </div>

              {/* 发送验证码按钮 */}
              <button
                onClick={handleSendCode}
                disabled={sendingCode}
                className="w-full h-14 bg-brand-900 hover:bg-brand-800 text-white rounded-2xl text-base font-bold shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {sendingCode ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <>
                    <span>{language === 'zh' ? '发送验证码' : 'Send Code'}</span>
                    <ArrowRight size={18} />
                  </>
                )}
              </button>

              {/* 返回登录 */}
              <div className="text-center">
                <button
                  onClick={onBackToLogin}
                  className="text-sm text-gray-400 hover:text-brand-900 font-medium"
                >
                  {language === 'zh' ? '返回登录' : 'Back to Login'}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: 输入验证码 */}
          {step === 'verify' && (
            <div className="space-y-6">
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                  {language === 'zh' ? '验证码' : 'Verification Code'}
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder={language === 'zh' ? '请输入验证码' : 'Enter code'}
                    maxLength={6}
                    className="flex-1 h-12 px-4 rounded-xl border border-surface-border bg-surface text-sm focus:bg-white focus:border-brand-900 focus:ring-4 focus:ring-brand-900/5 transition-all outline-none text-center tracking-widest text-lg"
                  />
                  <button
                    onClick={handleSendCode}
                    disabled={sendingCode || countdown > 0}
                    className="px-4 h-12 bg-surface border border-surface-border rounded-xl text-sm font-bold text-brand-900 hover:bg-surface-border disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap"
                  >
                    {sendingCode ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : countdown > 0 ? (
                      `${countdown}s`
                    ) : (
                      language === 'zh' ? '重新发送' : 'Resend'
                    )}
                  </button>
                </div>
              </div>

              <button
                onClick={handleVerifyCode}
                className="w-full h-14 bg-brand-900 hover:bg-brand-800 text-white rounded-2xl text-base font-bold shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3"
              >
                <span>{language === 'zh' ? '下一步' : 'Next'}</span>
                <ArrowRight size={18} />
              </button>
            </div>
          )}

          {/* Step 3: 设置新密码 */}
          {step === 'newPassword' && (
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                    {language === 'zh' ? '新密码' : 'New Password'}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={language === 'zh' ? '请输入新密码（至少6位）' : 'Enter new password (min 6 chars)'}
                      className="w-full h-12 px-4 pr-12 rounded-xl border border-surface-border bg-surface text-sm focus:bg-white focus:border-brand-900 focus:ring-4 focus:ring-brand-900/5 transition-all outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                    {language === 'zh' ? '确认密码' : 'Confirm Password'}
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={language === 'zh' ? '请再次输入新密码' : 'Confirm new password'}
                      className="w-full h-12 px-4 pr-12 rounded-xl border border-surface-border bg-surface text-sm focus:bg-white focus:border-brand-900 focus:ring-4 focus:ring-brand-900/5 transition-all outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              <button
                onClick={handleResetPassword}
                disabled={loading}
                className="w-full h-14 bg-brand-900 hover:bg-brand-800 text-white rounded-2xl text-base font-bold shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <>
                    <Lock size={18} />
                    <span>{mode === 'change' ? (language === 'zh' ? '修改密码' : 'Change Password') : (language === 'zh' ? '重置密码' : 'Reset Password')}</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step 4: 成功 */}
          {step === 'success' && (
            <div className="space-y-6 text-center">
              <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle size={40} className="text-green-500" />
              </div>
              <p className="text-gray-600">
                {mode === 'change'
                  ? (language === 'zh' ? '您的密码已成功修改' : 'Your password has been changed successfully.')
                  : (language === 'zh' ? '您的密码已成功重置，请使用新密码登录' : 'Your password has been reset. Please login with your new password.')}
              </p>
              <button
                onClick={onBackToLogin}
                className="w-full h-14 bg-brand-900 hover:bg-brand-800 text-white rounded-2xl text-base font-bold shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3"
              >
                <span>{mode === 'change' ? (language === 'zh' ? '完成' : 'Done') : (language === 'zh' ? '返回登录' : 'Back to Login')}</span>
                <ArrowRight size={18} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
